import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { currentCustomerSelector } from 'store/user/selectors';
import { shortenEmail } from 'utils';
import { useViewportSizing } from 'hooks/useViewportSizing';
import { ROUTES, ECOSYSTEM_STATUS } from 'enum';
import { NAVBAR_USER } from 'assets/Icons';
import { logout, acceptCommit, acceptRollback } from 'store/common-scenario-actions';
import CommitReminder from '../CommitReminder';
import CommitPopover from './CommitPopover';

import './header-links.scss';

const profileToggleSx = {
  color: 'inherit',
  textTransform: 'none',
  minWidth: 'auto',
  p: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
};

const HeaderLinks = ({
  showCommit: initialShowCommit,
  username,
  avatar,
  currentCustomer,
  ecosystem,
  logout,
  acceptCommit,
  acceptRollback,
  initialEcoLoaded,
  initialObjectLoaded,
}) => {
  const location = useLocation();
  const history = useHistory();
  const { cusID } = useParams();

  const { isSmallDesktop, isMediumDesktop } = useViewportSizing();

  const [anchorEl, setAnchorEl] = useState(null);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState(null);
  const [showCommit, setShowCommit] = useState(initialShowCommit);
  const [showReminder, setShowReminder] = useState(false);

  useEffect(() => {
    if (initialEcoLoaded && initialObjectLoaded) {
      setShowReminder(localStorage.getItem('commit-reminder') === 'true');
    }
  }, [initialEcoLoaded, initialObjectLoaded]);

  useEffect(() => {
    const commitStatus = ecosystem?.commitStatus;

    if (commitStatus === ECOSYSTEM_STATUS.COMMIT_ERROR) {
      setShowCommit(true);
      setAnchorEl(null);
    }

    if (initialShowCommit && ecosystem?.status === ECOSYSTEM_STATUS.PENDING_CHANGES) {
      setShowCommit(true);
    }
  }, [ecosystem, initialShowCommit]);

  const handleAcceptCommit = useCallback(
    (e) => {
      e.stopPropagation();
      setAnchorEl(null);
      setShowCommit(false);
      acceptCommit();
    },
    [acceptCommit],
  );

  const handleAcceptRollback = useCallback(
    (e) => {
      e.stopPropagation();
      setAnchorEl(null);
      setShowCommit(false);
      acceptRollback();
    },
    [acceptRollback],
  );

  const handleOpenCommit = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseCommit = () => {
    setAnchorEl(null);
  };

  const handleOpenProfileMenu = (event) => {
    setProfileMenuAnchorEl(event.currentTarget);
  };

  const handleCloseProfileMenu = () => {
    setProfileMenuAnchorEl(null);
  };

  const handleProfileNavigate = (target) => {
    handleCloseProfileMenu();
    history.push(target);
  };

  const handleOpenKnowledgeBase = () => {
    handleCloseProfileMenu();
    window.open(process.env.REACT_APP_KB_URL, '_blank');
  };

  const handleLogout = () => {
    handleCloseProfileMenu();
    logout();
  };

  const handleCloseReminder = (checked) => {
    if (checked) {
      localStorage.setItem('commit-reminder', 'false');
    }
    setShowReminder(false);
  };

  const commitShow = showCommit && ecosystem?.status === ECOSYSTEM_STATUS.PENDING_CHANGES;
  const commitDisabled = initialShowCommit && (!showCommit || ecosystem?.status !== ECOSYSTEM_STATUS.PENDING_CHANGES);

  return (
    <div>
      <Box component="ul" className="nav navbar-nav navbar-right">
        {commitDisabled && (
          <Box component="li" className="navbar-item">
            <div className="nav-profile commit-container">
              <div className="commit-box disabled">
                <span>Apply Changes</span>
              </div>
            </div>
          </Box>
        )}
        {commitShow && (
          <>
            <Box component="li" className="navbar-item">
              <div className="nav-profile commit-container">
                {commitShow && <div className="tooltip">Changes not in effect until applied</div>}
                <div className="commit-box" onClick={handleOpenCommit} data-testid="button-apply_changes">
                  <span>Apply Changes</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="8" cy="8" r="5" fill="#F3D7F8" />
                    <circle cx="8" cy="8" r="7.5" stroke="#F3D7F8" />
                  </svg>
                </div>
                <CommitPopover
                  anchorEl={anchorEl}
                  onClose={handleCloseCommit}
                  onCommit={handleAcceptCommit}
                  onRollback={handleAcceptRollback}
                />
              </div>
            </Box>
            {showReminder && <CommitReminder onClose={handleCloseReminder} />}
          </>
        )}
        <Box component="li" className="navbar-item last">
          <div className="flex-row nav-profile">
            <Box className="dropdown" id="dropdown-profile-options">
              <Button
                id="dropdown-basic"
                className="dropdown-toggle"
                aria-controls={profileMenuAnchorEl ? 'profile-options-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={profileMenuAnchorEl ? 'true' : undefined}
                onClick={handleOpenProfileMenu}
                sx={profileToggleSx}
              >
                <img
                  src={avatar || NAVBAR_USER}
                  alt="navbar-user"
                  className="navbar-user"
                  style={avatar ? { width: 36, height: 36 } : undefined}
                />
                <p>{isSmallDesktop || isMediumDesktop ? shortenEmail(username) : username}</p>
                <ExpandMoreIcon sx={{ color: '#6d8994' }} />
              </Button>

              <Menu
                id="profile-options-menu"
                anchorEl={profileMenuAnchorEl}
                open={!!profileMenuAnchorEl}
                onClose={handleCloseProfileMenu}
              >
                <MenuItem className="wedge-menu-item" onClick={() => handleProfileNavigate(ROUTES.PROFILE)}>
                  Profile
                </MenuItem>
                <MenuItem
                  className="wedge-menu-item"
                  onClick={() =>
                    handleProfileNavigate({ pathname: ROUTES.CUSTOMERS, state: { from: location.pathname } })
                  }
                >
                  {currentCustomer?.uuid ? `My Organization (${currentCustomer.name})` : 'My Organization'}
                </MenuItem>
                {cusID && (
                  <MenuItem
                    className="wedge-menu-item"
                    onClick={() => handleProfileNavigate(`${ROUTES.CUSTOMER_SUPPORT}/${cusID}`)}
                  >
                    Support Requests
                  </MenuItem>
                )}
                <MenuItem className="wedge-menu-item" onClick={handleOpenKnowledgeBase}>
                  Help / Knowledge Base
                </MenuItem>
                <MenuItem className="wedge-menu-item" onClick={handleLogout}>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </div>
        </Box>
      </Box>
    </div>
  );
};

HeaderLinks.defaultProps = {
  showCommit: false,
};

HeaderLinks.propTypes = {
  showCommit: PropTypes.bool,
  username: PropTypes.string.isRequired,
  avatar: PropTypes.string,
  currentCustomer: PropTypes.object,
  ecosystem: PropTypes.object,
  logout: PropTypes.func.isRequired,
  acceptCommit: PropTypes.func.isRequired,
  acceptRollback: PropTypes.func.isRequired,
  initialEcoLoaded: PropTypes.bool,
  initialObjectLoaded: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  currentCustomer: currentCustomerSelector(state),
  username: state.auth.username,
  avatar: state.user.avatar,
  ecosystem: state.ecosystems.currentEcosystem,
  initialEcoLoaded: state.ecosystems.initialEcoLoaded,
  initialObjectLoaded: state.objects.initialObjectLoaded,
});

const mapDispatchToProps = {
  logout,
  acceptCommit,
  acceptRollback,
};

export default connect(mapStateToProps, mapDispatchToProps)(HeaderLinks);
